import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import {
  Button,
  ButtonAppearance,
  Heading,
  Text,
  Icon,
  Tag,
  Divider,
  Link,
  LinkSize,
  LinkStyle,
  DropdownMenu,
} from '@atlassian/learning-components';

import { formatTimeRange, getTimeUntilStart, ISODateTimeString } from 'src/utils/date';

import { CalendarDate } from './CalendarDate';
import { CALENDAR_LABELS, BUTTON_LABELS, TEXT_LABELS } from './LiveTrainingCard';

export interface LiveTrainingCardDesktopProps {
  title: string;
  subtitle: string;
  startDateTime: ISODateTimeString;
  endDateTime: ISODateTimeString;
  isInPast: boolean;
  isCompleted: boolean;
  enrolled: boolean;
  products: string[];
  host: string;
  isFree: boolean;
  isSaved: boolean;
  videoCallUrl?: string;
  isCompletionCertificateLoading?: boolean;
  showAddToCalendar?: boolean;
  showDownloadMaterials?: boolean;
  showDownloadCompletionCertificate?: boolean;
  showTimeUntilStart?: boolean;
  hideButtonRow?: boolean;
  isCard?: boolean;

  // Handlers
  onDetailsClick?: () => void;
  onSave?: () => void;
  onAddToCalendar?: (label: string) => void;
  onDownloadMaterials?: () => void;
  onDownloadCompletionCertificate?: () => void;
  onJoinViaVideoCall?: () => void;

  // Tags
  tags: React.ReactNode[];
}

export const LiveTrainingCardDesktop = ({
  title,
  subtitle,
  startDateTime,
  endDateTime,
  isInPast,
  isCompleted,
  enrolled,
  products = [],
  host,
  isSaved,
  videoCallUrl,
  isCompletionCertificateLoading = false,
  showAddToCalendar = false,
  showDownloadMaterials = false,
  showDownloadCompletionCertificate = false,
  showTimeUntilStart = true,
  hideButtonRow = false,
  isCard = true,
  onDetailsClick,
  onSave,
  onAddToCalendar,
  onDownloadMaterials,
  onDownloadCompletionCertificate,
  onJoinViaVideoCall,
  tags,
}: LiveTrainingCardDesktopProps) => {
  const formattedDateTime = formatTimeRange(startDateTime, endDateTime);
  const timeUntilStart = getTimeUntilStart(startDateTime);

  // Build calendar dropdown menu items
  const calendarMenuItems = Object.values(CALENDAR_LABELS).reduce(
    (acc, label) => ({
      ...acc,
      [label]: {
        isDisabled: false,
        isSelected: false,
      },
    }),
    {},
  );

  const handleDetailsClick = (event: React.MouseEvent): void => {
    if (onDetailsClick) {
      event.preventDefault();
      onDetailsClick();
    }
  };

  const handleSave = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    if (onSave) {
      onSave();
    }
  };

  const handleDownloadMaterials = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    if (onDownloadMaterials) {
      onDownloadMaterials();
    }
  };

  const handleDownloadCompletionCertificate = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    if (onDownloadCompletionCertificate) {
      onDownloadCompletionCertificate();
    }
  };

  const handleJoinViaVideoCall = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    if (onJoinViaVideoCall) {
      onJoinViaVideoCall();
    }
  };

  return (
    <article data-testid="live-training-card-desktop" css={[containerStyles, isCard && cardStyles]}>
      {/* top row */}
      <div css={topRowStyles}>
        {/* left column */}
        <div css={css({ flexShrink: 0 })}>
          <CalendarDate date={startDateTime} isPast={isInPast} />
        </div>

        {/* right column */}
        <div css={mainContentStyles}>
          <div css={timeRowStyles}>
            <div css={timeRowContentStyles}>
              <Icon type="emoji-frequent" color={token('color.text.brand')} />
              {isInPast ? (
                <Text>{TEXT_LABELS.PAST}</Text>
              ) : (
                <>
                  <Text>{formattedDateTime}</Text>
                  {showTimeUntilStart && (
                    <>
                      <span css={css({ color: token('color.text.brand') })}>{` - `}</span>
                      <span css={tagStyles}>
                        <Text size="small" fontWeight="regular">
                          Starting in: <span css={tagBoldStyles}>{timeUntilStart}</span>
                        </Text>
                      </span>
                    </>
                  )}
                </>
              )}
            </div>
            <div css={tagsAndSaveButtonStyles}>
              <div css={desktopTagsStyles}>{tags}</div>
              {onSave && (
                <Button
                  label={BUTTON_LABELS.SAVE}
                  appearance={ButtonAppearance.ICON}
                  iconBefore={
                    <Icon
                      type={isSaved ? 'bookmark-narrow-filled' : 'bookmark-narrow'}
                      color={token('color.icon.brand')}
                    />
                  }
                  onClick={handleSave}
                />
              )}
            </div>
          </div>

          <div css={titleContainerStyles}>
            {!isCard ? (
              <div css={titleStyles} onClick={handleDetailsClick}>
                <Heading level={4}>{title}</Heading>
              </div>
            ) : (
              <Link
                link="#"
                label={title}
                size={LinkSize.EXTRA_LARGE}
                style={LinkStyle.SEMIBOLD}
                target="_self"
                icon="arrow-right-small"
                onClick={handleDetailsClick}
              />
            )}
          </div>

          <div css={subtitleStyles}>
            <Text>{subtitle}</Text>
          </div>
          <div css={metadataRowStyles}>
            <div css={tagListStyles}>
              {products &&
                products.length > 0 &&
                products.map((product) => <Tag key={product} label={product} isTall />)}
            </div>
            <div css={hostStyles}>
              <Icon type="person" size="small" color={token('color.icon.brand')} />
              <Text size="small">{host}</Text>
            </div>
          </div>
        </div>
      </div>

      {!hideButtonRow && (
        <>
          <div css={dividerStyles}>
            <Divider color={token('color.border')} />
          </div>

          {/* bottom row */}
          <div css={buttonRowStyles}>
            <div css={css({ flexGrow: 1 })}>
              {showAddToCalendar && !isInPast && onAddToCalendar && (
                <DropdownMenu
                  customTriggerComponent={({ triggerRef, ...triggerButtonProps }) => (
                    <span ref={triggerRef} {...triggerButtonProps}>
                      <Button
                        label={BUTTON_LABELS.ADD_TO_CALENDAR}
                        appearance={ButtonAppearance.LINK}
                        iconBefore={<Icon type="add" color={token('color.icon.brand')} />}
                        onClick={triggerButtonProps.onClick}
                      />
                    </span>
                  )}
                  isOptionMenu
                  items={calendarMenuItems}
                  onItemClick={onAddToCalendar}
                />
              )}
            </div>
            {showDownloadMaterials && onDownloadMaterials && (
              <Button
                label={BUTTON_LABELS.COURSE_MATERIALS}
                appearance={ButtonAppearance.LINK}
                iconBefore={<Icon type="download-core" color={token('color.icon.brand')} />}
                onClick={handleDownloadMaterials}
              />
            )}
            {isCompleted && showDownloadCompletionCertificate && onDownloadCompletionCertificate && (
              <Button
                label={BUTTON_LABELS.COMPLETION_CERTIFICATE}
                appearance={ButtonAppearance.SECONDARY}
                iconBefore={<Icon type="download-core" color={token('color.icon.brand')} />}
                onClick={handleDownloadCompletionCertificate}
                isLoading={isCompletionCertificateLoading}
              />
            )}
            {enrolled && !isInPast && Boolean(videoCallUrl?.length) && onJoinViaVideoCall && (
              <Button
                label={BUTTON_LABELS.JOIN_VIDEO_CALL}
                appearance={ButtonAppearance.PRIMARY}
                iconAfter={<Icon type="link-external" color={token('color.icon.inverse')} />}
                onClick={handleJoinViaVideoCall}
              />
            )}
          </div>
        </>
      )}
    </article>
  );
};

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  padding: token('space.200'),
});

const cardStyles = css({
  borderRadius: '8px',
  boxShadow: token('elevation.shadow.raised'),
  border: `1px solid ${token('color.border')}`,
});

const titleContainerStyles = css({
  '& span:first-of-type': {
    verticalAlign: 'middle',
  },

  '& svg < span': {
    verticalAlign: 'text-top',
  },

  '& a': {
    color: token('color.link'),
    fontFamily: 'Charlie Display, sans-serif',
  },

  '& a:hover': {
    textDecoration: 'none',
    color: token('color.link.pressed'),
  },
});

const timeRowStyles = css({
  display: 'flex',
  flexDirection: 'row',
});

const timeRowContentStyles = css({
  display: 'flex',
  flexDirection: 'row',
  gap: token('space.100'),
  flexGrow: 1,
  alignItems: 'center',
});

const mainContentStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.050'),
  flexGrow: 1,
});

const titleStyles = css({
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
});

const subtitleStyles = css({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const topRowStyles = css({
  display: 'flex',
  flexDirection: 'row',
  gap: token('space.300'),
});

const metadataRowStyles = css({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginTop: token('space.200'),
});

const hostStyles = css({
  display: 'flex',
  flexDirection: 'row',
  gap: token('space.100'),
  alignItems: 'center',
  minWidth: '184px',

  '& svg': {
    color: token('color.icon.brand'),
  },
});

const tagsAndSaveButtonStyles = css({
  flexShrink: 0,
  alignContent: 'center',
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
  gap: token('space.100'),
});

const desktopTagsStyles = css({
  display: 'flex',
  flexDirection: 'row',
  gap: token('space.100'),
});

const tagListStyles = css({
  display: 'flex',
  flexDirection: 'row',
  gap: token('space.100'),
});

const tagStyles = css({
  border: `1px solid ${token('color.border')}`,
  color: token('color.text.accent.gray.bolder'),
  borderRadius: '4px',
  padding: `${token('space.050')} ${token('space.100')}`,

  '& > span': {
    verticalAlign: 'middle',
  },
});

const tagBoldStyles = css({
  fontWeight: 'bold',
  color: token('color.text.brand'),
});

const buttonRowStyles = css({
  display: 'flex',
  flexDirection: 'row',
  gap: token('space.100'),

  // join via video call button
  '& [data-testid="button-primary"] svg': {
    color: token('color.icon.inverse'),
  },

  // completion certificate button
  '& [data-testid="button-secondary"] svg': {
    color: token('color.icon.brand'),
    width: '21px',
    height: '21px',
  },

  '& [data-testid="button-secondary"]:hover svg': {
    color: token('color.icon.inverse'),
  },

  // course materials button
  '& [data-testid="button-link"]': {
    height: 'fit-content',
    alignSelf: 'center',
    paddingTop: token('space.100'),
    paddingBottom: token('space.100'),
  },

  '& [data-testid="button-link"] svg': {
    color: token('color.icon.brand'),
    width: '21px',
    height: '21px',
  },
});

const dividerStyles = css({
  margin: `${token('space.200')} 0`,
});
